import { useEffect, useState } from "react";
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from "recharts";
import { TrendingUp, TrendingDown, Minus, X, Filter } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import MetricCard from "@/components/dashboard/MetricCard";

const nz = new Intl.NumberFormat("en-NZ");
const monthLabel = (m) =>
    new Date(`${m}-01T00:00:00Z`).toLocaleString("en-NZ", { month: "short", year: "2-digit", timeZone: "UTC" });

export default function DemandDashboard() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({ crop: "", region: "", month: "" });

    useEffect(() => {
        let cancel = false;
        setLoading(true);
        (async () => {
            try {
                const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
                const { data } = await api.get("/metrics/demand", { params });
                if (!cancel) setData(data);
            } catch (e) {
                if (!cancel) setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => { cancel = true; };
    }, [filters]);

    if (err) {
        return (
            <div className="p-8">
                <div className="hg-card p-5 border-[#ef4444]/40">
                    <div className="hg-eyebrow text-[#fecaca]">// Error</div>
                    <div className="font-mono-data mt-1">{err}</div>
                </div>
            </div>
        );
    }
    if (!data) return <Skeleton />;

    const s = data.summary;
    const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
    const active = Object.values(filters).some(Boolean);
    const scopeLabel = [filters.crop, filters.region].filter(Boolean).join(" · ") || "All crops · All regions";

    return (
        <div className="min-h-full px-4 md:px-8 py-6 md:py-8" data-testid="demand-dashboard">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 pb-6 border-b border-white/5">
                <div>
                    <div className="hg-eyebrow mb-2">// Section 04 · Demand</div>
                    <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#FAFAFA] leading-none">
                        Demand Per Region
                    </h1>
                    <p className="mt-2 text-sm text-[#A1A1AA]">
                        Crop requests by region and month, with 90-day growth trajectory.
                    </p>
                </div>
                <div className="hidden md:flex items-center gap-2 hg-eyebrow">
                    <span className={`w-1.5 h-1.5 rounded-full bg-[#22C55E] ${loading ? "animate-ping" : "hg-pulse-dot"}`} />
                    Updated {new Date(data.updated_at).toLocaleTimeString()}
                </div>
            </header>

            {/* Filter bar */}
            <section className="hg-card p-4 mt-6 flex flex-col md:flex-row md:items-center gap-3" data-testid="demand-filters">
                <div className="flex items-center gap-2 hg-eyebrow shrink-0"><Filter size={12} className="text-[#22C55E]" /> Filter</div>
                <Select testid="filter-crop" value={filters.crop} onChange={set("crop")} placeholder="All crops"
                    options={data.crops.map((c) => ({ v: c, l: c }))} />
                <Select testid="filter-region" value={filters.region} onChange={set("region")} placeholder="All regions"
                    options={data.regions.map((r) => ({ v: r.region, l: `${r.region} (${r.code})` }))} />
                <Select testid="filter-month" value={filters.month} onChange={set("month")} placeholder="All months"
                    options={[...data.months].reverse().map((m) => ({ v: m, l: monthLabel(m) }))} />
                {active && (
                    <button
                        onClick={() => setFilters({ crop: "", region: "", month: "" })}
                        className="flex items-center gap-1.5 px-3 py-2 font-mono-data text-[11px] uppercase tracking-widest text-[#A1A1AA] hover:text-[#fecaca] border border-white/10 hover:border-[#ef4444]/50 rounded-sm transition-colors"
                        data-testid="filter-clear"
                    >
                        <X size={12} /> Clear
                    </button>
                )}
                <div className="md:ml-auto hg-eyebrow text-[10px]" data-testid="filter-row-count">{s.row_count} rows</div>
            </section>

            {/* KPI strip */}
            <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <MetricCard label="// Requests · current month" value={nz.format(s.current_month_requests)} accent
                    deltaPct={s.mom_change_pct} deltaLabel="prev month" testid="metric-current-requests" />
                <MetricCard label="// 90-day trajectory" value={`${s.trend_90d_change_pct > 0 ? "+" : ""}${s.trend_90d_change_pct}%`}
                    sublabel="growth" testid="metric-90d-change" />
                <div className="hg-card p-5 flex flex-col gap-2" data-testid="metric-top-pair">
                    <div className="hg-eyebrow">// Hottest pairing</div>
                    {s.top_pair ? (
                        <>
                            <div className="font-mono-data text-2xl text-[#FAFAFA]">{s.top_pair.crop}</div>
                            <div className="font-mono-data text-xs text-[#A1A1AA]">
                                {s.top_pair.region} · <span className="text-[#22C55E]">{nz.format(s.top_pair.requests)}</span> requests
                            </div>
                        </>
                    ) : <div className="font-mono-data text-sm text-[#52525b]">No data in scope</div>}
                </div>
            </section>

            {/* Trend chart */}
            <section className="hg-card p-5 mt-4" data-testid="demand-trend-chart">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="hg-eyebrow">// 90-day demand trend</div>
                        <div className="font-heading text-xl text-[#FAFAFA] mt-1">{scopeLabel}</div>
                    </div>
                    <TrendPill trend={s.trend_90d_change_pct > 1.5 ? "up" : s.trend_90d_change_pct < -1.5 ? "down" : "stable"} changePct={s.trend_90d_change_pct} />
                </div>
                <div className="h-[260px] mt-4">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data.trend_90d} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                            <defs>
                                <linearGradient id="demandFill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#22C55E" stopOpacity={0.4} />
                                    <stop offset="100%" stopColor="#22C55E" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                            <XAxis dataKey="date" tickLine={false} axisLine={false} interval={14}
                                tick={{ fill: "#71717a", fontSize: 10, fontFamily: "JetBrains Mono, monospace" }}
                                tickFormatter={(d) => d.slice(5)} />
                            <YAxis tickLine={false} axisLine={false} domain={["auto", "auto"]}
                                tick={{ fill: "#71717a", fontSize: 10, fontFamily: "JetBrains Mono, monospace" }}
                                tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : Math.round(v))} />
                            <ReferenceLine y={data.trend_90d[0]?.value} stroke="rgba(255,255,255,0.15)" strokeDasharray="3 3" />
                            <Tooltip content={<DarkTooltip />} />
                            <Area type="monotone" dataKey="value" stroke="#22C55E" strokeWidth={1.6} fill="url(#demandFill)" dot={false}
                                activeDot={{ r: 3, fill: "#22C55E", stroke: "#050505" }} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </section>

            {/* Table */}
            <section className="hg-card p-5 mt-4 overflow-x-auto" data-testid="demand-table">
                <div className="flex items-center justify-between pb-3 border-b border-white/5">
                    <div className="hg-eyebrow">// Demand ledger · crop × region × month</div>
                    <div className="hg-eyebrow text-[10px]">{data.rows.length} of {s.row_count}</div>
                </div>
                <table className="w-full mt-1 text-left">
                    <thead>
                        <tr className="hg-eyebrow text-[9px]">
                            <th className="py-2 font-normal">Month</th>
                            <th className="py-2 font-normal">Crop</th>
                            <th className="py-2 font-normal">Region</th>
                            <th className="py-2 font-normal text-right">Requests</th>
                            <th className="py-2 font-normal text-right">MoM</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.length === 0 && (
                            <tr><td colSpan={5} className="py-8 text-center font-mono-data text-sm text-[#52525b]" data-testid="demand-empty">No rows match these filters</td></tr>
                        )}
                        {data.rows.map((r) => (
                            <tr key={r.id} className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors" data-testid={`demand-row-${r.id}`}>
                                <td className="py-2 font-mono-data text-[11px] text-[#A1A1AA]">{monthLabel(r.month)}</td>
                                <td className="py-2">
                                    <button onClick={() => set("crop")(r.crop)} className="font-mono-data text-xs text-[#FAFAFA] hover:text-[#22C55E] transition-colors">{r.crop}</button>
                                </td>
                                <td className="py-2">
                                    <button onClick={() => set("region")(r.region)} className="font-mono-data text-xs text-[#FAFAFA] hover:text-[#22C55E] transition-colors">{r.region}</button>
                                    <span className="hg-eyebrow text-[9px] ml-2">{r.country} · {r.code}</span>
                                </td>
                                <td className="py-2 text-right font-mono-data text-xs text-[#FAFAFA]">{nz.format(r.requests)}</td>
                                <td className="py-2 text-right"><TrendPill trend={r.trend} changePct={r.change_pct} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>
        </div>
    );
}

const Select = ({ value, onChange, options, placeholder, testid }) => (
    <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-[#0A0A0A] border border-white/10 focus:border-[#22C55E]/60 outline-none rounded-sm px-3 py-2 font-mono-data text-[11px] uppercase tracking-wider text-[#FAFAFA] min-w-[160px]"
        data-testid={testid}
    >
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
);

const TrendPill = ({ trend, changePct }) => {
    const map = {
        up: { Icon: TrendingUp, color: "#22C55E" },
        down: { Icon: TrendingDown, color: "#ef4444" },
        stable: { Icon: Minus, color: "#A1A1AA" },
    };
    const { Icon, color } = map[trend] || map.stable;
    return (
        <span className="inline-flex items-center gap-1 font-mono-data text-[11px] justify-end" style={{ color }}>
            <Icon size={11} />{changePct > 0 ? "+" : ""}{Number(changePct).toFixed(1)}%
        </span>
    );
};

const DarkTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
        <div className="hg-glass px-3 py-2 rounded-sm border border-white/10">
            <div className="hg-eyebrow text-[9px]">{p.date}</div>
            <div className="font-mono-data text-xs text-[#FAFAFA]">{nz.format(Math.round(p.value))} requests/day</div>
        </div>
    );
};

const Skeleton = () => (
    <div className="p-8 space-y-4">
        <div className="h-12 w-80 bg-white/5 rounded-sm animate-pulse" />
        <div className="hg-card h-[64px] animate-pulse" />
        <div className="grid grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => <div key={i} className="hg-card h-[120px] animate-pulse" />)}
        </div>
        <div className="hg-card h-[320px] animate-pulse" />
    </div>
);
